import { EXCLUSIVE_FILTER_GROUPS } from '../config'
import type { ConsignmentFilters } from '../model'

// Helper function to check if a filter is active
const isFilterActive = (filterKey: string, filters: ConsignmentFilters): boolean => {
  const filterValue = filters[filterKey as keyof ConsignmentFilters]
  if (Array.isArray(filterValue)) {
    return filterValue.length > 0
  } else if (typeof filterValue === 'string') {
    return filterValue !== ''
  }
  return false
}

// Get all group indices a filter belongs to (handles overlapping groups)
const getFilterGroupIndices = (filterKey: string): number[] => {
  const indices: number[] = []
  for (let i = 0; i < EXCLUSIVE_FILTER_GROUPS.length; i++) {
    if (EXCLUSIVE_FILTER_GROUPS[i].some((f) => f.key === filterKey)) {
      indices.push(i)
    }
  }
  return indices
}

// Check if two filters share at least one common group
const filtersShareGroup = (filterKeyA: string, filterKeyB: string): boolean => {
  const groupsA = getFilterGroupIndices(filterKeyA)
  const groupsB = getFilterGroupIndices(filterKeyB)
  return groupsA.some((g) => groupsB.includes(g))
}

// Get filter label from EXCLUSIVE_FILTER_GROUPS
const getFilterLabel = (filterKey: string): string => {
  for (const group of EXCLUSIVE_FILTER_GROUPS) {
    const filter = group.find((f) => f.key === filterKey)
    if (filter) return filter.label
  }
  return filterKey
}

// Returns a map of disabled filters with their tooltip messages
export const getDisabledFilters = (currentFilters: ConsignmentFilters): Record<string, string> => {
  const disabledFilters: Record<string, string> = {}

  // Get all unique filter keys from all groups
  const allFilterKeys = new Set<string>()
  for (const group of EXCLUSIVE_FILTER_GROUPS) {
    for (const filter of group) {
      allFilterKeys.add(filter.key)
    }
  }

  // Get all active filter keys
  const activeFilterKeys: string[] = []
  for (const key of allFilterKeys) {
    if (isFilterActive(key, currentFilters)) {
      activeFilterKeys.push(key)
    }
  }

  // For each filter, check if it conflicts with any active filter
  for (const filterKey of allFilterKeys) {
    // Skip if filter is already active
    if (isFilterActive(filterKey, currentFilters)) continue

    // Find active filters that DON'T share any group with this filter
    const conflictingLabels: string[] = []
    for (const activeKey of activeFilterKeys) {
      if (!filtersShareGroup(filterKey, activeKey)) {
        conflictingLabels.push(getFilterLabel(activeKey))
      }
    }

    if (conflictingLabels.length > 0) {
      // Remove duplicates
      const uniqueLabels = [...new Set(conflictingLabels)]

      // Generate tooltip message
      let conflictMessage: string
      if (uniqueLabels.length === 1) {
        conflictMessage = uniqueLabels[0]
      } else if (uniqueLabels.length === 2) {
        conflictMessage = `${uniqueLabels[0]} and ${uniqueLabels[1]}`
      } else {
        const lastFilter = uniqueLabels.pop()
        conflictMessage = `${uniqueLabels.join(', ')} and ${lastFilter}`
      }

      disabledFilters[filterKey] =
        `${getFilterLabel(filterKey)} is unavailable. Remove ${conflictMessage} to enable it.`
    }
  }

  return disabledFilters
}
